import { useLang } from 'hooks/lang';

import { Column, Row, IconButton } from 'components/Blocks';
import { XIcon } from 'components/Icons';

import { ExtraPercentage } from './ExtraPercentage';
import { ClientBalances } from './ClientBalances';

import { FormControlDispatchAction } from 'hooks';

import { calculateBalancesTotal, calculateTotalValue, extractFixedInvoiceData } from './utils';
import { invoiceCountriesConfig } from 'utils/countries';
import { formatCurrency } from 'utils/text';

import { ClientBalanceEntry } from 'entities';
import { InvoiceCreationProps, BaseInvoiceProps } from './types';

interface InvoiceValueProps {
  children?: never;
  entities: BaseInvoiceProps[];
  state: InvoiceCreationProps;
  dispatch: FormControlDispatchAction<InvoiceCreationProps>;
  allowBalances: boolean;
}

function sumTotal(
  projectInvoices: BaseInvoiceProps[],
  clientBalances: ClientBalanceEntry[],
): string {
  const value = projectInvoices.reduce((acc, next) => acc + Number(next.value), 0);

  const withBalances = value + calculateBalancesTotal(clientBalances);

  return withBalances.toFixed(2);
}

export function InvoiceValue({
  entities,
  allowBalances,
  ...stateProps
}: InvoiceValueProps): JSX.Element {
  const { t } = useLang();

  const { country, currency } = extractFixedInvoiceData(entities);

  const total = sumTotal(entities, stateProps.state.clientBalances);

  const { percentageConfig } = invoiceCountriesConfig[country];

  const finalValue = calculateTotalValue(
    total,
    percentageConfig.addToTotal ? stateProps.state.additionalPercentage : undefined,
    percentageConfig.addToTotal ? percentageConfig.customTotalCalculator : undefined,
  );

  const removeBalance = (id: string): void => {
    stateProps.dispatch({
      clientBalances: ({ clientBalances }) =>
        clientBalances.filter((existing) => existing.id !== id),
    });
  };

  return (
    <Column>
      {allowBalances && <ClientBalances total={total} {...stateProps} entities={entities} />}

      <Column gap="3">
        {entities.map(({ value, name, id }) => (
          <Row justifyContent="space-between" key={id}>
            <span>{name}</span>

            <span>{formatCurrency(value, currency)}</span>
          </Row>
        ))}

        {stateProps.state.clientBalances.map(({ delta, id, type }) => (
          <Row justifyContent="space-between" key={`balance-overview-${id}`}>
            <Row gap="1">
              <span>{t('client-balance')}</span>

              <IconButton onClick={() => removeBalance(id)} className="text-destructive">
                <XIcon size={14} />
              </IconButton>
            </Row>

            <span
              className={type === 'OVERPAYMENT' ? 'text-success-light' : 'text-destructive-light'}
            >
              {`${type === 'OVERPAYMENT' ? '-' : '+'} ${formatCurrency(delta, currency)}`}
            </span>
          </Row>
        ))}

        <hr className="w-full" />

        <Row justifyContent="space-between">
          <span>{t('entries-total')}</span>

          <span>{formatCurrency(total, currency)}</span>
        </Row>

        <ExtraPercentage country={country} {...stateProps} />

        <hr className="w-full" />

        <Row justifyContent="space-between">
          <span className="font-medium">{t('total')}</span>

          <span className="font-medium text-primary">{formatCurrency(finalValue, currency)}</span>
        </Row>
      </Column>
    </Column>
  );
}
